import { useState } from 'react'
import { toast } from 'sonner'
import { Button, Drawer, Input, Select } from '@/components/ui'
import { useCreateRequest } from '../api/queries'
import type { Category, Priority, RequestType } from '../api/types'
import { CATEGORY_OPTIONS, PRIORITY_OPTIONS } from '../utils/meta'

export interface NewCardDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (id: string) => void
  /** When set, the card is fixed to this request_type and the selector is
   *  hidden — the two sibling modules each lock their own type. */
  lockedRequestType?: RequestType
}

export function NewCardDrawer({
  open,
  onOpenChange,
  onCreated,
  lockedRequestType,
}: NewCardDrawerProps) {
  const create = useCreateRequest()
  const [requestType, setRequestType] = useState<RequestType>(
    lockedRequestType ?? 'customer',
  )
  const [category, setCategory] = useState<Category>('coa')
  const [title, setTitle] = useState('')
  const [partnerName, setPartnerName] = useState('')
  const [products, setProducts] = useState('')
  const [lots, setLots] = useState('')
  const [priority, setPriority] = useState<Priority>('medium')
  const [assignedTo, setAssignedTo] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [notes, setNotes] = useState('')

  const reset = () => {
    setTitle('')
    setPartnerName('')
    setProducts('')
    setLots('')
    setNotes('')
    setDueDate('')
    setAssignedTo('')
    setPriority('medium')
  }

  const submit = () => {
    if (!title.trim()) {
      toast.error('Title is required')
      return
    }
    create.mutate(
      {
        request_type: requestType,
        category,
        title: title.trim(),
        priority,
        assigned_to: assignedTo ? (assignedTo as 'frank' | 'shamola') : null,
        partner_name: partnerName || null,
        product_names: products
          ? products.split(',').map((s) => s.trim()).filter(Boolean)
          : null,
        lot_names: lots
          ? lots.split(',').map((s) => s.trim()).filter(Boolean)
          : null,
        due_date: dueDate || null,
        notes: notes || null,
      },
      {
        onSuccess: (card) => {
          toast.success('Card created')
          reset()
          onOpenChange(false)
          onCreated(card.id)
        },
        onError: (e) => toast.error(String(e)),
      },
    )
  }

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      size="standard"
      title="New regulatory card"
      footer={
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" loading={create.isPending} onClick={submit}>
            Create card
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        {lockedRequestType ? null : (
          <Field label="Request type">
            <Select
              value={requestType}
              onChange={(e) => setRequestType(e.target.value as RequestType)}
            >
              <option value="customer">Customer request</option>
              <option value="vendor">Vendor request</option>
            </Select>
          </Field>
        )}
        <Field label="Category">
          <Select value={category} onChange={(e) => setCategory(e.target.value as Category)}>
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Title">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Sunday Riley — COA Request" />
        </Field>
        <Field label="Customer / Vendor name">
          <Input value={partnerName} onChange={(e) => setPartnerName(e.target.value)} />
        </Field>
        <Field label="Products (comma-separated)">
          <Input value={products} onChange={(e) => setProducts(e.target.value)} />
        </Field>
        <Field label="Lots (comma-separated)">
          <Input value={lots} onChange={(e) => setLots(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Priority">
            <Select value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
              {PRIORITY_OPTIONS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Assigned to">
            <Select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}>
              <option value="">Unassigned</option>
              <option value="frank">Frank</option>
              <option value="shamola">Shamola</option>
            </Select>
          </Field>
        </div>
        <Field label="Due date (auto if blank)">
          <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </Field>
        <Field label="Notes">
          <textarea
            className="min-h-[64px] w-full rounded-sm border border-border-default bg-surface p-2 text-sm text-primary"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>
      </div>
    </Drawer>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-role-metadata text-tertiary">{label}</span>
      {children}
    </label>
  )
}
